/**
 * Прогон мониторинга — общее у кнопки в шапке и у диалога (docs/release-2026-09-19.md §3.6):
 * опрос списка обходов и чистые функции подписи строк таблицы.
 *
 * ОДИН ОПРОС НА ДВА ОРГАНА, А НЕ ДВА ПАРАЛЛЕЛЬНЫХ. Кнопка в шапке опрашивает
 * `GET /api/v1/monitoring/runs?limit=1` ради одного бита — «идёт ли скан» (дышащая точка);
 * диалог — тот же маршрут с окном обходов для выбора прогона. Пока диалог открыт, опрос
 * кнопки выключен (`enabled: false`): шапка всё равно под затемнением, а два таймера на один
 * маршрут — это двойная нагрузка ровно тогда, когда скан идёт и опрос частый. Закрыли
 * диалог — кнопка опрашивает сразу, не дожидаясь своего таймера.
 *
 * ПОЧЕМУ НЕ `useResource`. Тот делает один запрос на путь и обновляется явным refetch — а
 * здесь нужен переменный интервал, посчитанный по ПОСЛЕДНЕМУ ответу (5 с, пока скан идёт;
 * 15 с или ничего — без него), ровно как у `RunStatusBar`. Гонка ответов гасится
 * AbortController, размонтирование снимает и запрос, и таймер.
 *
 * ТОЛЬКО ЧТО ЗАКАЗАННЫЙ СКАН ЖИВЁТ РЯДОМ С ОТВЕТОМ ОПРОСА (`noteScan`). POST скана возвращает
 * саму заявку, и экран обязан показать «Мониторинг идёт…» в момент ответа, а не следующим
 * опросом. Ответ GET, ушедший ДО коммита POST, может приехать ПОСЛЕ него и принести прежний
 * (закрытый) скан — тогда он проиграл бы свежей заявке и молча выключил бы частый опрос.
 * Поэтому показывается более свежий из двух по моменту создания (`fresherScan`).
 *
 * ОТВЕТ РАЗБИРАЕТСЯ СХЕМОЙ, а не приводится типом: кнопка живёт в шапке на каждом экране, и
 * чужой ответ (бэкенд без этой партии отвечает 404, прокси — HTML) обязан давать кнопку без
 * точки, а не белый экран. Сбой опроса проглатывается молча, как у `RunStatusBar`: красная
 * плашка в шапке из-за одного неудачного GET научила бы её не замечать.
 *
 * ВРЕМЯ — только через `@smm/contracts` (правило R11). Подпись «19.09, 09:12» в зоне бренда —
 * `wallShort` из `components/format.ts`, единственного дома форматирования времени (переехала
 * туда 19.09.2026 первой же правкой того файла).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { get } from '../api/client.ts';
import {
  type MonitoringItem,
  type MonitoringRunKind,
  type MonitoringRunSummary,
  type MonitoringRunsResponse,
  MonitoringRunsResponseSchema,
  type MonitoringScan,
} from '../api/types.ts';
import { wallShort } from './format.ts';

/** Пока скан идёт — раз в 5 с (§3.6): обход длится минуты, секундная долбёжка не нужна. */
export const SCAN_POLL_MS = 5000;

/**
 * Шапка без скана — раз в 15 с. Не «на всякий случай»: скан мог заказать второй человек в
 * другой вкладке, и точка на кнопке обязана это показать. Число то же, что у `RunStatusBar`.
 */
export const IDLE_POLL_MS = 15_000;

/** Скан ещё не кончился: заявка в очереди или мониторинг её уже взял. */
export const scanActive = (scan: MonitoringScan | null): boolean =>
  scan !== null && (scan.state === 'queued' || scan.state === 'taken');

/**
 * Из двух снимков заявки скана — более свежий. Одна и та же заявка — побеждает снимок опроса:
 * он пришёл позже ответа POST и знает её исход. Разные — побеждает созданная позже;
 * `created_at` — ISO_UTC_MS фиксированной ширины, поэтому сравнение строк и есть сравнение
 * моментов (R11: без разбора времени), а при равенстве решает UUIDv7 — он растёт со временем.
 */
export function fresherScan(
  polled: MonitoringScan | null,
  noted: MonitoringScan | null,
): MonitoringScan | null {
  if (noted === null) return polled;
  if (polled === null) return noted;
  if (polled.request_id === noted.request_id) return polled;
  if (polled.created_at !== noted.created_at) {
    return polled.created_at > noted.created_at ? polled : noted;
  }
  return polled.request_id > noted.request_id ? polled : noted;
}

export interface MonitoringRunsPollOptions {
  /** Сколько обходов просить: кнопке хватает одного, диалогу нужно окно для выбора. */
  readonly limit: number;
  /** `false` — не опрашивать вовсе (кнопка, пока диалог открыт и опрашивает сам). */
  readonly enabled: boolean;
  /**
   * Задержка опроса без скана. `null` — без скана не опрашивать: диалог обновляется «при
   * открытии и после каждого действия» (§3.6), а не по таймеру.
   */
  readonly idleMs: number | null;
}

export interface MonitoringRunsPoll {
  /** Последний разобранный ответ; `undefined` — ещё не пришёл (или пришёл чужой). */
  readonly data: MonitoringRunsResponse | undefined;
  /** Отказ последнего опроса. Прежний ответ при этом остаётся — экран не мигает пустотой. */
  readonly error: Error | undefined;
  /** Скан, который видит экран: свежий из ответа опроса и из `noteScan`. */
  readonly scan: MonitoringScan | null;
  readonly scanning: boolean;
  /** Опросить сейчас (после действия), не дожидаясь таймера. */
  readonly refresh: () => void;
  /** Ответ POST скана: показать «идёт» сразу и перейти на частый опрос. */
  readonly noteScan: (scan: MonitoringScan) => void;
}

/**
 * Снимок, привязанный к адресу. Бренд стоит в адресе, а шапка переживает переход между
 * брендами: без привязки ответ одного бренда показывал бы точку скана под другим, пока не
 * приедет свой.
 */
interface Pinned<T> {
  readonly path: string;
  readonly value: T;
}

export function useMonitoringRuns(
  brand: string,
  { limit, enabled, idleMs }: MonitoringRunsPollOptions,
): MonitoringRunsPoll {
  const path = `/api/v1/monitoring/runs?brand=${encodeURIComponent(brand)}&limit=${String(limit)}`;
  const [data, setData] = useState<Pinned<MonitoringRunsResponse> | null>(null);
  const [error, setError] = useState<Pinned<Error> | null>(null);
  const [noted, setNoted] = useState<Pinned<MonitoringScan> | null>(null);
  /** Читается в момент планирования следующего опроса — то есть по последнему ответу. */
  const notedRef = useRef<Pinned<MonitoringScan> | null>(null);
  /** Частый ли опрос. Меняется только по УСПЕШНОМУ ответу: сбой сети не выключает скан. */
  const fast = useRef(false);
  /**
   * «Опросить сейчас» живого цикла: снять запрос в полёте и таймер, спросить заново. Ссылка,
   * а не счётчик в зависимостях эффекта: перезапуск эффекта ради внешнего события — ровно
   * то, что правило useExhaustiveDependencies считает ошибкой, и оно право. `null` — цикла
   * нет (опрос выключен или компонент снят), и «сейчас» некому исполнить.
   */
  const kick = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let ac = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const tick = (): void => {
      // Свой контроллер у каждого запроса: ответ, снятый перезапуском, не имеет права ни
      // записать состояние, ни запланировать следующий опрос поверх нового.
      const own = ac;
      get<unknown>(path, own.signal)
        .then((body) => {
          if (own.signal.aborted) return;
          const parsed = MonitoringRunsResponseSchema.safeParse(body);
          if (!parsed.success) throw new Error('ответ сервера не разобран');
          setData({ path, value: parsed.data });
          setError(null);
          const mine = notedRef.current?.path === path ? notedRef.current.value : null;
          fast.current = scanActive(fresherScan(parsed.data.scan, mine));
        })
        .catch((e: unknown) => {
          if (own.signal.aborted) return;
          setError({ path, value: e instanceof Error ? e : new Error(String(e)) });
        })
        .finally(() => {
          if (stopped || own.signal.aborted) return;
          const delay = fast.current ? SCAN_POLL_MS : idleMs;
          if (delay !== null) timer = setTimeout(tick, delay);
        });
    };

    kick.current = () => {
      ac.abort();
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      ac = new AbortController();
      tick();
    };
    tick();

    return () => {
      stopped = true;
      kick.current = null;
      ac.abort();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [path, enabled, idleMs]);

  const refresh = useCallback(() => {
    kick.current?.();
  }, []);

  const noteScan = useCallback(
    (scan: MonitoringScan) => {
      const pinned = { path, value: scan };
      notedRef.current = pinned;
      fast.current = scanActive(scan);
      setNoted(pinned);
      kick.current?.();
    },
    [path],
  );

  const current = data?.path === path ? data.value : undefined;
  const scan = fresherScan(current?.scan ?? null, noted?.path === path ? noted.value : null);
  return {
    data: current,
    error: error?.path === path ? error.value : undefined,
    scan,
    scanning: scanActive(scan),
    refresh,
    noteScan,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Сорвавшийся скан, которого человек не видел (ревью партии, U-B4)
//
// Скан длится минуты, и диалог к его концу обычно закрыт. Раньше об отказе знал только
// открытый диалог, видевший скан идущим (`watched` в `MonitoringRunDialog`): закрыл диалог —
// отказ не сообщался никогда, а погасшая точка читалась как «обход прошёл». Теперь знание
// «какой исход человек уже видел» живёт вне диалога, в sessionStorage по бренду: кнопка шапки
// (она опрашивает, пока диалог закрыт) рисует красную точку, диалог при открытии показывает
// отказ словами и тем самым отмечает его просмотренным.
//
// ПОЧЕМУ ХРАНИЛИЩЕ, А НЕ СОСТОЯНИЕ КНОПКИ. `Shell` рисует каждая страница сама, и переход
// между вкладками пересоздаёт кнопку вместе с её состоянием: скан, заказанный на «Бэклоге» и
// сорвавшийся на «Мониторинге», снова не был бы сообщён. sessionStorage переживает и переходы,
// и перезагрузку страницы, но не выходит за вкладку.
//
// ПЕРВОЕ ЗНАКОМСТВО — НЕ НОВОСТЬ. Вкладка, впервые увидевшая сканы бренда, считает уже
// закончившийся скан просмотренным: красная точка про вчерашний сбой в каждой новой вкладке
// научила бы её не замечать. Идущий скан — наоборот: его исход человеку ещё предстоит узнать.
// Нет хранилища (запрет данных сайта) — точки нет, а открытый диалог сообщает отказ скана,
// который видел идущим, как прежде: деградация до старого поведения, а не падение.

/** Скан в той мере, в какой его нужно помнить: какой и когда заведён. */
export interface ScanMark {
  readonly request_id: string;
  readonly created_at: string;
}

/**
 * Что вкладка знает об исходах сканов бренда. `seen` — последний закончившийся скан, исход
 * которого человеку показан (или который кончился до знакомства вкладки со сканами); `null` —
 * такого нет: сканов не было или первый увиденный ещё шёл.
 */
export interface ScanAck {
  readonly seen: ScanMark | null;
}

const scanAckKey = (brand: string): string => `smm.monitoring.scan-seen.${brand}`;

/**
 * Хранилище — у `window`, а не у `globalThis`, и в try: та же форма, что у хелперов
 * `BacklogAside`/`MonitoringPage` (в Node 26 свой глобальный Web Storage перекрывает jsdom;
 * доступ бросает при запрете данных сайта).
 */
function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

const isMark = (v: unknown): v is ScanMark =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as { request_id?: unknown }).request_id === 'string' &&
  typeof (v as { created_at?: unknown }).created_at === 'string';

/** `null` — вкладка со сканами бренда ещё не знакомилась (или хранилища нет, или запись чужая). */
export function readScanAck(brand: string): ScanAck | null {
  try {
    const raw = session()?.getItem(scanAckKey(brand)) ?? null;
    if (raw === null) return null;
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v === null || !('seen' in v)) return null;
    const seen = (v as { seen: unknown }).seen;
    if (seen === null) return { seen: null };
    return isMark(seen)
      ? { seen: { request_id: seen.request_id, created_at: seen.created_at } }
      : null;
  } catch {
    return null;
  }
}

function writeScanAck(brand: string, ack: ScanAck): void {
  try {
    session()?.setItem(scanAckKey(brand), JSON.stringify(ack));
  } catch {
    /* см. session(): молчим намеренно — точка лишь удобство */
  }
}

/** `a` заведён позже `b` — порядок `fresherScan`: момент создания, при равенстве — UUIDv7. */
const createdAfter = (a: ScanMark, b: ScanMark): boolean =>
  a.created_at !== b.created_at ? a.created_at > b.created_at : a.request_id > b.request_id;

const markOf = (scan: MonitoringScan): ScanMark => ({
  request_id: scan.request_id,
  created_at: scan.created_at,
});

/**
 * Скан сорвался, а человек этого ещё не видел. Смотрится только свежайший скан бренда (его и
 * отдаёт маршрут): новый скан поверх сорвавшегося — уже другой разговор, и о нём скажет его
 * собственный исход.
 */
export function unseenFailure(scan: MonitoringScan | null, ack: ScanAck | null): boolean {
  if (scan === null || scan.state !== 'failed' || ack === null) return false;
  return ack.seen === null || createdAfter(scan, ack.seen);
}

/**
 * Один ответ опроса глазами уведомления. Вкладка знакомится со сканами бренда (один раз, см.
 * «первое знакомство»), а `viewing` — диалог открыт и показывает исход — отмечает
 * закончившийся скан просмотренным. Возвращает, был ли скан сорвавшимся и непросмотренным ДО
 * этого ответа: диалогу это повод сказать об отказе словами.
 */
export function observeScan(brand: string, scan: MonitoringScan | null, viewing: boolean): boolean {
  const stored = readScanAck(brand);
  const before: ScanAck = stored ?? {
    seen: scan === null || scanActive(scan) ? null : markOf(scan),
  };
  const unseen = unseenFailure(scan, before);
  const shown =
    viewing &&
    scan !== null &&
    !scanActive(scan) &&
    (before.seen === null || createdAfter(scan, before.seen));
  const after: ScanAck = shown ? { seen: markOf(scan) } : before;
  if (stored === null || after !== before) writeScanAck(brand, after);
  return unseen;
}

// ─────────────────────────────────────────────────────────────────────────────
// Подписи

/**
 * Оценка словами экрана: `rating × 10` с одним знаком после запятой — «8,4» (§3.2). Округление
 * до десятых ДО `toFixed`: двоичная дробь 0.845 иначе печатается то «8,4», то «8,5».
 */
export function ratingText(rating: number): string {
  const tenths = Math.round(Math.min(1, Math.max(0, rating)) * 100) / 10;
  return tenths.toFixed(1).replace('.', ',');
}

/** Вид обхода — одним словом: подпись живёт в компактном списке прогонов. */
export const RUN_KIND_WORD: Readonly<Record<MonitoringRunKind, string>> = {
  scheduled: 'плановый',
  manual: 'ручной',
  scan: 'скан',
};

/**
 * Состояние обхода — только то, что отличается от нормы. «ok» не подписывается: иначе
 * слово стояло бы у каждой строки и сообщало бы, что ничего не произошло.
 */
const RUN_STATE_WORD: Readonly<Record<MonitoringRunSummary['state'], string | null>> = {
  running: 'идёт',
  ok: null,
  partial: 'частично',
  failed: 'сбой',
  overlapped: 'наложился',
};

export const runStateWord = (run: MonitoringRunSummary): string | null => RUN_STATE_WORD[run.state];

/** Строка списка прогонов: «19.09, 09:12 · скан · 42» (+ « · идёт» у незаконченного). */
export function runOptionLabel(run: MonitoringRunSummary, timezone: string | null): string {
  const state = runStateWord(run);
  const base = `${wallShort(run.started_at, timezone)} · ${RUN_KIND_WORD[run.kind]} · ${String(run.items_total)}`;
  return state === null ? base : `${base} · ${state}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Сортировка и выбор

export type SortKey = 'rating' | 'date';
export type SortDir = 'asc' | 'desc';

export interface SortOrder {
  readonly key: SortKey;
  readonly dir: SortDir;
}

/** По умолчанию — как отдаёт сервер: по убыванию оценки, при равенстве — свежие первыми. */
export const DEFAULT_SORT: SortOrder = { key: 'rating', dir: 'desc' };

const byDate = (a: MonitoringItem, b: MonitoringItem): number =>
  a.published_at === b.published_at ? 0 : a.published_at < b.published_at ? -1 : 1;

/**
 * Порядок строк. Вторичный ключ объявлен, а не оставлен устойчивости сортировки: у оценки
 * это дата (свежие первыми — тот же порядок, что у сервера), у даты — оценка (сильнее
 * первыми). Даты сравниваются строкой ISO_UTC_MS — это и есть сравнение моментов.
 */
export function sortItems(items: readonly MonitoringItem[], order: SortOrder): MonitoringItem[] {
  const sign = order.dir === 'desc' ? -1 : 1;
  return [...items].sort((a, b) => {
    if (order.key === 'rating') {
      if (a.rating !== b.rating) return sign * (a.rating - b.rating);
      return -byDate(a, b);
    }
    const d = byDate(a, b);
    if (d !== 0) return sign * d;
    return b.rating - a.rating;
  });
}

/**
 * Строку можно заказать, пока по статье нет ни живого поста, ни идущей заявки, ни живой темы:
 * `none` и `failed` (прошлая попытка сорвалась — пробуем снова). Ровно у них есть ☐ и
 * «Написать». `topic` — тему по статье уже завёл сам мониторинг, и писатель её возьмёт:
 * второй заказ оплатил бы второй пост о том же (ревью партии, находка B1).
 */
export const isPickable = (item: MonitoringItem): boolean =>
  item.status.kind === 'none' || item.status.kind === 'failed';
